import { EventEmitter, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { OrderChangedState } from "../model/enums/order-changed-state";
import { StorageKeys } from "../model/enums/storage-keys";
import { Order } from "../model/interfaces/order";
import { OrderUpdateAction } from "../model/interfaces/order-update-action";
import { Share } from "../model/interfaces/share";
import { TicketInstance } from "../model/interfaces/ticket-instance";
import { TicketPackageInstance } from "../model/interfaces/ticket-package-instance";
import { OrderService } from "./order.service";
import { StorageService } from "./storage.service";

@Injectable({ providedIn: 'root' })
export class NetsPaymentService
{
    private readonly orderService: OrderService;
    private readonly storageService: StorageService;

    public constructor(orderService: OrderService, storageService: StorageService)
    {
        this.orderService = orderService;
        this.storageService = storageService;
        this.init();
    }

    public getOrderForPayment(createOrder: Observable<Order>, refOrder: Order  | Array<TicketInstance>): Observable<Order>
    {
        const pendingOrderId = this.storageService.getItem<number>(StorageKeys.NetsPendingPaymentOrderId);

        if(pendingOrderId == null)
        {
            return createOrder;
        }

        return this.orderService.getOrderById(pendingOrderId).pipe(switchMap(pendingOrder => {

            if(pendingOrder != null)
            {
                let originalShareList: Share[] = null;
                if(refOrder instanceof Array)
                {
                    originalShareList = refOrder.map(i => i.Share);
                } else {
                    originalShareList = refOrder.TicketPackageInstances.reduce((accumulator: Share[], ticketPackageInstance: TicketPackageInstance) =>
                    {
                        return [...ticketPackageInstance.Shares, ...accumulator];
                    }, [...refOrder.Shares]);
                }
                    
                const pendingShareList = [...pendingOrder.Instances.map(s => s.Share)];

                const getShareListKey = (shares: Share[]): string => {
                    let shareKeyList = shares.map((share) => { return share.Ticket.TicketNumber + share.Name; });
                    return shareKeyList
                            .sort((sKey1, sKey2) => { return sKey1 > sKey2 ? 1 : -1})
                            .reduce((acc, sKey) => { return acc + sKey }, '') 
                };
                
                if(getShareListKey(originalShareList) === getShareListKey(pendingShareList))
                {
                    return of(pendingOrder);
                }
            }
            
            return createOrder;
        }));
    }

    public setPendingOrder(orderId: number)
    {
        this.storageService.setItem(StorageKeys.NetsPendingPaymentOrderId, orderId);
    }

    private init()
    {
        this.orderService.onOrderChangedState.subscribe((state: OrderChangedState) => {
            this.clearPendingOrder();
        });

        this.orderService.onOrderCompleted.subscribe((state: OrderChangedState) => {
            this.clearPendingOrder();
        });

        this.orderService.onOrderContentUpdated.subscribe((args: {order: Order, action: OrderUpdateAction}) => {
            if(args.action.actionType !== 'none' && args.action.actionType !== 'create')
            {
                this.clearPendingOrder();
            }
        });
    }

    public clearPendingOrder(): void
    {
        this.storageService.removeItem(StorageKeys.NetsPendingPaymentOrderId);
    }
}
